import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { getUserTimezone, convertLocalTimeToUTC } from "./utils/timezoneHelper";

export default function AddReminder() {
  const navigate = useNavigate();
  const { logUserActivity } = useActivityLogger();
  const [category, setCategory] = useState("Minum obat");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Medicine states
  const [medicineName, setMedicineName] = useState("");
  const [medicineFrequency, setMedicineFrequency] = useState("1x sehari");
  const [medicineUnit, setMedicineUnit] = useState("tablet");
  const [medicineRule, setMedicineRule] = useState("sesudah makan");
  const [medicineTimes, setMedicineTimes] = useState([
    { time: "", quantity: 1 },
  ]);

  // Therapy states
  const [therapyType, setTherapyType] = useState("");
  const [therapyPlace, setTherapyPlace] = useState("");
  const [therapyFrequency, setTherapyFrequency] = useState("1x seminggu");
  const [therapyTime, setTherapyTime] = useState("");
  const [therapySchedules, setTherapySchedules] = useState([
    { day: "Senin", time: "" },
  ]);

  // Control states
  const [doctorName, setDoctorName] = useState("");
  const [doctorSpecialization, setDoctorSpecialization] = useState("");
  const [facilityType, setFacilityType] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [controlDate, setControlDate] = useState("");
  const [controlFrequency, setControlFrequency] = useState("1x sebulan");

  // Meal states
  const [mealFrequency, setMealFrequency] = useState("1x sehari");
  const [mealSchedules, setMealSchedules] = useState([
    {
      time: "",
      karbohidrat: "",
      protein: "",
      lemak: "",
      vitamin: "",
      serat: "",
    },
  ]);

  const handleMedicineFrequencyChange = (value) => {
    // logClick("medicine_frequency", `Changed to ${value}`);
    // logUserActivity("reminder", "medicine_frequency_change", "AddReminder", `Changed to ${value}`);
    setMedicineFrequency(value);
    const count = parseInt(value[0]);
    const newTimes = Array.from(
      { length: count },
      (_, i) => medicineTimes[i] || { time: "", quantity: 1 }
    );
    setMedicineTimes(newTimes);
  };

  const handleMealFrequencyChange = (value) => {
    // logClick("meal_frequency", `Changed to ${value}`);
    // logUserActivity("reminder", "meal_frequency_change", "AddReminder", `Changed to ${value}`);
    setMealFrequency(value);
    const count = parseInt(value[0]);
    const newSchedules = Array.from(
      { length: count },
      (_, i) =>
        mealSchedules[i] || {
          time: "",
          karbohidrat: "",
          protein: "",
          lemak: "",
          vitamin: "",
          serat: "",
        }
    );
    setMealSchedules(newSchedules);
  };

  const handleTimeChange = (index, field, value) => {
    // logClick(`medicine_time_${field}`, `Changed to ${value} at index ${index}`);
    // logUserActivity("reminder", `medicine_time_${field}_change`, "AddReminder", `Changed to ${value} at index ${index}`);
    const updated = [...medicineTimes];
    updated[index][field] = value;
    setMedicineTimes(updated);
  };

  const handleMealScheduleChange = (index, field, value) => {
    // logClick(`meal_schedule_${field}`, `Changed to ${value} at index ${index}`);
    // logUserActivity("reminder", `meal_schedule_${field}_change`, "AddReminder", `Changed to ${value} at index ${index}`);
    const updated = [...mealSchedules];
    updated[index][field] = value;
    setMealSchedules(updated);
  };

  const handleTherapyFrequencyChange = (value) => {
    setTherapyFrequency(value);
    const count = parseInt(value[0]);
    const days = [
      "Senin",
      "Selasa",
      "Rabu",
      "Kamis",
      "Jumat",
      "Sabtu",
      "Minggu",
    ];
    const newSchedules = Array.from(
      { length: count },
      (_, i) => therapySchedules[i] || { day: days[i] || "Senin", time: "" }
    );
    setTherapySchedules(newSchedules);
  };

  const handleTherapyScheduleChange = (index, field, value) => {
    const updated = [...therapySchedules];
    updated[index][field] = value;
    setTherapySchedules(updated);
  };

  const validateForm = () => {
    setError("");

    switch (category) {
      case "Minum obat":
        if (!medicineName.trim()) {
          setError("Nama obat harus diisi");
          return false;
        }
        for (let i = 0; i < medicineTimes.length; i++) {
          if (!medicineTimes[i].time || !medicineTimes[i].quantity) {
            setError(`Jam dan jumlah obat ke-${i + 1} harus diisi`);
            return false;
          }
          // Validate time format
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(medicineTimes[i].time)) {
            setError(`Format jam obat ke-${i + 1} tidak valid`);
            return false;
          }
        }
        break;

      case "Terapi":
        if (!therapyType.trim() || !therapyPlace.trim()) {
          setError("Jenis terapi dan tempat terapi harus diisi");
          return false;
        }
        for (let i = 0; i < therapySchedules.length; i++) {
          if (!therapySchedules[i].time || !therapySchedules[i].day) {
            setError(`Hari dan jam terapi ke-${i + 1} harus diisi`);
            return false;
          }
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(therapySchedules[i].time)) {
            setError(`Format jam terapi ke-${i + 1} tidak valid`);
            return false;
          }
        }
        break;
      case "Kontrol ke dokter":
        if (!doctorName.trim() || !facilityName.trim() || !controlDate) {
          setError(
            "Nama dokter, fasilitas kesehatan, dan tanggal kontrol harus diisi"
          );
          return false;
        }
        // Validate datetime format
        const controlDateTime = new Date(controlDate);
        if (isNaN(controlDateTime.getTime())) {
          setError("Format tanggal kontrol tidak valid");
          return false;
        }
        // Check if date is in the future
        if (controlDateTime < new Date()) {
          setError("Tanggal kontrol harus di masa depan");
          return false;
        }
        break;

      case "Makan":
        if (!mealSchedules[0]?.time) {
          setError("Jam makan pertama harus diisi");
          setLoading(false);
          return;
        }
        const mealTimeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!mealTimeRegex.test(mealSchedules[0].time)) {
          setError("Format jam makan tidak valid");
          setLoading(false);
          return;
        }
        const [mealHours, mealMinutes] = mealSchedules[0].time
          .split(":")
          .map(Number);
        const todayMeal = new Date();
        const mealDateTime = new Date(
          todayMeal.getFullYear(),
          todayMeal.getMonth(),
          todayMeal.getDate(),
          mealHours,
          mealMinutes
        );
        if (isNaN(mealDateTime.getTime())) {
          setError("Format jam makan tidak valid");
          setLoading(false);
          return;
        }
        break;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      let response;
      const userTimezone = getUserTimezone();
      switch (category) {
        case "Minum obat":
          // Use the specific medicine endpoint
          const medicineData = {
            judul: `Minum ${medicineName}`,
            timezone: userTimezone,
            details: {
              nama_obat: medicineName,
              frekuensi: medicineFrequency,
              jadwal: medicineTimes.map((time) => ({
                jam: time.time,
                jumlah: parseInt(time.quantity),
                satuan: medicineUnit,
              })),
              aturan: medicineRule,
            },
          };
          response = await api.post("/pengingat/obat", medicineData);
          // logFormSubmit("medicine_reminder", true);
          logUserActivity(
            "reminder",
            "medicine_reminder",
            "AddReminder",
            `Created reminder for ${medicineName}`
          );
          break;

        case "Terapi":
          // Validate therapyTime before creating date
          // if (!therapyTime) {
          //   setError("Jam terapi harus diisi");
          //   setLoading(false);
          //   return;
          // }

          const firstSchedule = therapySchedules[0];
          const today = new Date();
          const [hours, minutes] = firstSchedule.time.split(":");
          const therapyDateTime = convertLocalTimeToUTC(
            firstSchedule.time,
            today,
            userTimezone
          );

          const requestData = {
            tipe: "terapi",
            judul: `${therapyType} di ${therapyPlace}`,
            waktu: therapyDateTime.toISOString(),
            timezone: userTimezone,
            details: {
              jenis_terapi: therapyType,
              tempat_terapi: therapyPlace,
              frekuensi: therapyFrequency,
              jadwal: therapySchedules.map((schedule) => ({
                hari: schedule.day,
                jam: schedule.time,
              })),
            },
          };
          response = await api.post("/pengingat", requestData);
          // logFormSubmit("therapy_reminder", true);
          logUserActivity(
            "reminder",
            "therapy_reminder",
            "AddReminder",
            `Created reminder for ${requestData} at ${therapyPlace}`
          );
          break;

        case "Kontrol ke dokter":
          if (!controlDate) {
            setError("Tanggal kontrol harus diisi");
            setLoading(false);
            return;
          }
          const controlDateTime = new Date(controlDate);
          if (isNaN(controlDateTime.getTime())) {
            setError("Format tanggal kontrol tidak valid");
            setLoading(false);
            return;
          }

          const controlData = {
            tipe: "kontrol",
            judul: `Kontrol ke ${doctorName}`,
            waktu: controlDateTime.toISOString(),
            timezone: userTimezone,
            details: {
              nama_dokter: doctorName,
              spesialisasi: doctorSpecialization,
              jenis_faskes: facilityType,
              nama_faskes: facilityName,
              jadwal_kontrol: {
                tanggal_waktu: controlDateTime.toISOString(),
              },
              frekuensi_kontrol: controlFrequency,
              jadwal_pengingat: [{ offset: "1h" }, { offset: "1d" }],
            },
          };
          response = await api.post("/pengingat", controlData);
          // logFormSubmit("doctor_control_reminder", true);
          logUserActivity(
            "reminder",
            "doctor_control_reminder",
            "AddReminder",
            `Created reminder for control with ${doctorName}`
          );
          break;

        case "Makan":
          // Validate meal schedules before creating date
          if (!mealSchedules[0]?.time) {
            setError("Jam makan pertama harus diisi");
            setLoading(false);
            return;
          }

          // Create date for today with the first meal time
          const todayMeal = new Date();
          const [mealHours, mealMinutes] = mealSchedules[0].time.split(":");
          const mealDateTime = convertLocalTimeToUTC(
            mealSchedules[0].time,
            todayMeal,
            userTimezone
          );

          if (isNaN(mealDateTime.getTime())) {
            setError("Format jam makan tidak valid");
            setLoading(false);
            return;
          }

          const mealData = {
            tipe: "makan",
            judul: `Jadwal Makan (${mealFrequency})`,
            waktu: mealDateTime.toISOString(),
            timezone: userTimezone,
            details: {
              frekuensi: mealFrequency,
              jadwal: mealSchedules.map((schedule) => ({
                jam: schedule.time,
                sudah_makan: false,
                karbohidrat: schedule.karbohidrat || "",
                protein: schedule.protein || "",
                lemak: schedule.lemak || "",
                vitamin: schedule.vitamin || "",
                serat: schedule.serat || "",
              })),
            },
          };
          response = await api.post("/pengingat", mealData);
          // logFormSubmit("meal_reminder", true);
          logUserActivity(
            "reminder",
            "meal_reminder",
            "AddReminder",
            `Created meal reminder with frequency ${mealFrequency}`
          );
          break;

        default:
          // logFormSubmit("invalid_category", false);
          logUserActivity(
            "reminder",
            "invalid_category",
            "AddReminder",
            `Invalid category selected: ${category}`
          );
          setError("Kategori pengingat tidak valid");

          setLoading(false);

          return;
      }

      // Success - navigate back to reminders page
      navigate("/reminders");
    } catch (err) {
      // logFormSubmit("reminder_creation", false);
      logUserActivity(
        "reminder",
        "reminder_creation_error",
        "AddReminder",
        `Failed to create reminder: ${err.message}`
      );
      console.error("Failed to create reminder:", err);
      setError(err.response?.data?.error || "Gagal membuat pengingat");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4 pb-32 flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)}>
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Buat Pengingat</h1>
        <div className="h-6 w-6" />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col flex-grow space-y-5 overflow-y-auto pb-32"
      >
        <div>
          <label className="text-sm font-medium mb-2 block">
            Kategori Pengingat
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2 bg-white"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={loading}
          >
            <option>Minum obat</option>
            <option>Terapi</option>
            <option>Kontrol ke dokter</option>
            <option>Makan</option>
          </select>
        </div>

        {category === "Minum obat" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Obat *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Tensicap"
                value={medicineName}
                onChange={(e) => setMedicineName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Minum Obat
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineFrequency}
                onChange={(e) => handleMedicineFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x sehari</option>
                <option>2x sehari</option>
                <option>3x sehari</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Satuan Obat
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineUnit}
                onChange={(e) => setMedicineUnit(e.target.value)}
                disabled={loading}
              >
                <option>tablet</option>
                <option>kapsul</option>
                <option>sirup</option>
                <option>tetes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Aturan Minum
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={medicineRule}
                onChange={(e) => setMedicineRule(e.target.value)}
                disabled={loading}
              >
                <option>sesudah makan</option>
                <option>sebelum makan</option>
                <option>bersama makan</option>
              </select>
            </div>
            <div className="space-y-3">
              {medicineTimes.map((entry, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <label>Jam *</label>
                    <input
                      type="time"
                      value={entry.time}
                      onChange={(e) =>
                        handleTimeChange(index, "time", e.target.value)
                      }
                      className="border rounded px-2 py-1 text-sm"
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      value={entry.quantity}
                      onChange={(e) =>
                        handleTimeChange(index, "quantity", e.target.value)
                      }
                      className="w-12 border rounded px-2 py-1 text-sm text-center"
                      disabled={loading}
                      required
                    />
                    <span className="text-sm text-gray-700">
                      {medicineUnit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {category === "Terapi" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jenis Terapi *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Terapi fisik"
                value={therapyType}
                onChange={(e) => setTherapyType(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Tempat Terapi *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Klinik"
                value={therapyPlace}
                onChange={(e) => setTherapyPlace(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Terapi
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={therapyFrequency}
                onChange={(e) => handleTherapyFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x seminggu</option>
                <option>2x seminggu</option>
                <option>3x seminggu</option>
                <option>4x seminggu</option>
                <option>5x seminggu</option>
                <option>6x seminggu</option>
                <option>7x seminggu</option>
              </select>
            </div>

            {/* Jadwal Terapi */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jadwal Terapi
              </label>
              <div className="space-y-3">
                {therapySchedules.map((schedule, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 border rounded-lg p-3"
                  >
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">
                        Hari ke-{index + 1}
                      </label>
                      <select
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={schedule.day}
                        onChange={(e) =>
                          handleTherapyScheduleChange(
                            index,
                            "day",
                            e.target.value
                          )
                        }
                        disabled={loading}
                        required
                      >
                        <option value="Senin">Senin</option>
                        <option value="Selasa">Selasa</option>
                        <option value="Rabu">Rabu</option>
                        <option value="Kamis">Kamis</option>
                        <option value="Jumat">Jumat</option>
                        <option value="Sabtu">Sabtu</option>
                        <option value="Minggu">Minggu</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">
                        Jam *
                      </label>
                      <input
                        type="time"
                        value={schedule.time}
                        onChange={(e) =>
                          handleTherapyScheduleChange(
                            index,
                            "time",
                            e.target.value
                          )
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {category === "Kontrol ke dokter" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Dokter *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Dr. Shakira"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Spesialisasi Dokter
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Neurologist"
                value={doctorSpecialization}
                onChange={(e) => setDoctorSpecialization(e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jenis Fasilitas Kesehatan
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={facilityType}
                onChange={(e) => setFacilityType(e.target.value)}
                disabled={loading}
              >
                <option value="">Pilih jenis fasilitas kesehatan</option>
                <option value="Rumah Sakit">Rumah Sakit/RS</option>
                <option value="Klinik">Klinik</option>
                <option value="Puskesmas">Puskesmas</option>
                <option value="Lainnya">Lainnya</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Nama Fasilitas Kesehatan *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                placeholder="RS Hermina Manado"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Jadwal Kontrol Terdekat *
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="datetime-local"
                value={controlDate}
                onChange={(e) => setControlDate(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Kontrol
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={controlFrequency}
                onChange={(e) => setControlFrequency(e.target.value)}
                disabled={loading}
              >
                <option>1x sebulan</option>
                <option>1x dalam 3 bulan</option>
                <option>1x dalam 6 bulan</option>
              </select>
            </div>
          </>
        )}

        {category === "Makan" && (
          <>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Frekuensi Makan
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-white"
                value={mealFrequency}
                onChange={(e) => handleMealFrequencyChange(e.target.value)}
                disabled={loading}
              >
                <option>1x sehari</option>
                <option>2x sehari</option>
                <option>3x sehari</option>
              </select>
            </div>

            {mealSchedules.map((schedule, i) => (
              <div key={i}>
                <hr className="my-3" />
                <label className="text-sm font-medium mb-2 block">
                  Jadwal Makan ke-{i + 1} *
                </label>
                <div className="mb-2">
                  <input
                    className="w-full border rounded-lg px-3 py-2"
                    type="time"
                    value={schedule.time}
                    onChange={(e) =>
                      handleMealScheduleChange(i, "time", e.target.value)
                    }
                    disabled={loading}
                    required
                  />
                </div>
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Karbohidrat (opsional)"
                  value={schedule.karbohidrat}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "karbohidrat", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Protein (opsional)"
                  value={schedule.protein}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "protein", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Lemak (opsional)"
                  value={schedule.lemak}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "lemak", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Vitamin & Mineral (opsional)"
                  value={schedule.vitamin}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "vitamin", e.target.value)
                  }
                  disabled={loading}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-1"
                  placeholder="Serat (opsional)"
                  value={schedule.serat}
                  onChange={(e) =>
                    handleMealScheduleChange(i, "serat", e.target.value)
                  }
                  disabled={loading}
                />
              </div>
            ))}
          </>
        )}
      </form>

      <div className="fixed bottom-0 left-0 w-full bg-white px-4 py-4 border-t flex flex-col space-y-3">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-blue-500 text-white rounded-lg py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Menyimpan..." : "Simpan"}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={loading}
          className="w-full border border-gray-300 text-gray-700 rounded-lg py-3 font-medium disabled:opacity-50"
        >
          Batal
        </button>
      </div>
    </div>
  );
}
